import { useState, useRef, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { X } from 'lucide-react';
import { state } from '../../store/global';
import API from '../../lib/api';

/**
 * SmsVerifyModal — modal de verificacion SMS reutilizable para acciones que
 * mueven dinero hacia afuera (cross-border, envios, retiros, Kamino).
 *
 * Patron: al abrir envia el codigo SMS, el usuario lo ingresa, se verifica
 * con Twilio (auth/verifyCode REAL), y al verificar llama onVerified().
 *
 * Props:
 *  - visible: boolean — muestra/oculta el modal
 *  - onVerified: () => void — se ejecuta SOLO si el codigo es correcto
 *  - onClose: () => void — cancelar
 *  - title?: string — titulo del modal
 *  - description?: string — texto que explica que se va a autorizar
 */
export default function SmsVerifyModal({
  visible,
  onVerified,
  onClose,
  title = 'Verificacion de seguridad',
  description = 'Para autorizar esta operacion necesitamos verificar tu identidad.',
}) {
  const snap = useSnapshot(state);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sent, setSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const inputsRef = useRef([]);

  const phone = snap.user?.phonenumber
    ? `${snap.user.phonenumber.nationality?.area_code || ''}${snap.user.phonenumber.number || ''}`
    : '';

  // Enviar codigo al abrir
  useEffect(() => {
    if (visible && !sent) {
      sendCode();
    }
    if (!visible) {
      // reset al cerrar
      setCode(['', '', '', '', '', '']);
      setError(false);
      setSent(false);
      setResendTimer(0);
    }
  }, [visible]);

  // Timer de reenvio
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const sendCode = async () => {
    if (!phone) { setError(true); return; }
    setSending(true); setError(false);
    try {
      await API.post('auth/sendCodeToPhone', { phonenumber: phone });
      setSent(true);
      setResendTimer(60);
      setTimeout(() => inputsRef.current[0]?.focus(), 200);
    } catch (err) {
      console.log('[SmsVerifyModal] send error:', err?.response?.data?.message || err.message);
      setError(true);
    } finally {
      setSending(false);
    }
  };

  const onChange = (idx, val) => {
    if (val.length > 1) val = val.slice(-1);
    if (!/^\d*$/.test(val)) return;
    const next = [...code]; next[idx] = val; setCode(next); setError(false);
    if (val && idx < 5) inputsRef.current[idx + 1]?.focus();
  };

  const onKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) inputsRef.current[idx - 1]?.focus();
  };

  const onVerify = async () => {
    const full = code.join('');
    if (full.length !== 6) { setError(true); return; }
    setVerifying(true); setError(false);
    try {
      const res = await API.post('auth/verifyCode', { phonenumber: phone, code: full });
      if (res.data && res.data.phoneVerified === true) {
        onVerified();
      } else {
        setError(true);
        setCode(['', '', '', '', '', '']);
        inputsRef.current[0]?.focus();
      }
    } catch (err) {
      console.log('[SmsVerifyModal] verify error:', err?.response?.data?.message || err.message);
      setError(true);
      setCode(['', '', '', '', '', '']);
      inputsRef.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-[#181D27]">{title}</h3>
          <button onClick={onClose} className="text-[#535862] hover:text-[#181D27]">
            <X size={22} />
          </button>
        </div>

        <p className="text-sm text-[#535862] mb-4">{description}</p>

        {!phone ? (
          <p className="text-sm text-[#B42318]">No tienes telefono registrado. Actualiza tu perfil.</p>
        ) : (
          <>
            <p className="text-xs text-[#535862] text-center mb-1">
              Codigo de 6 digitos enviado a
            </p>
            <p className="text-sm font-medium text-[#181D27] text-center mb-4">
              {snap.user.phonenumber?.nationality?.area_code} *****{snap.user.phonenumber?.number?.slice(-4)}
            </p>

            <div className="flex justify-center gap-2 mb-4">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputsRef.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => onChange(index, e.target.value)}
                  onKeyDown={(e) => onKeyDown(index, e)}
                  className={`w-11 h-12 text-center text-lg font-semibold border-2 rounded-lg focus:outline-none transition-colors ${
                    error ? 'border-[#D92D20] bg-[#FEF3F2]' : 'border-[#E1E1E1] focus:border-[#092F7B]'
                  }`}
                />
              ))}
            </div>

            {error && (
              <div className="mb-3 p-2.5 bg-[#FEF3F2] border border-[#FEE4E2] rounded-lg">
                <p className="text-xs text-[#B42318] text-center font-medium">
                  Codigo incorrecto o expirado. Intenta de nuevo.
                </p>
              </div>
            )}

            <button
              onClick={onVerify}
              disabled={verifying || code.join('').length !== 6}
              className="w-full px-4 py-3 text-sm font-medium text-white bg-[#092F7B] rounded-lg hover:bg-[#0a3a94] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {verifying ? 'Verificando...' : 'Verificar y continuar'}
            </button>

            <div className="text-center mt-3">
              {resendTimer > 0 ? (
                <span className="text-xs text-[#9CA3AF]">Reenviar codigo en {resendTimer}s</span>
              ) : (
                <button onClick={sendCode} disabled={sending} className="text-xs text-[#092F7B] font-medium hover:underline">
                  {sending ? 'Enviando...' : 'Reenviar codigo'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
